import { Component, Inject, OnInit, HostListener, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { ImageViewerService } from 'app/shared/services/image-viewer.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface ImageLightboxDialogData {
    imageUrl: string;
    alt?: string;
    images?: string[];
    currentIndex?: number;
}

@Component({
    selector: 'app-image-lightbox-dialog',
    standalone: true,
    imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatTooltipModule, MatProgressSpinnerModule],
    templateUrl: './image-lightbox-dialog.component.html',
    styleUrls: ['./image-lightbox-dialog.component.scss'],
})
export class ImageLightboxDialogComponent implements OnInit, OnDestroy {
    currentImageUrl: string | null = null;
    currentIndex: number = 0;
    images: string[] = [];
    zoomLevel: number = 1;
    loading: boolean = true;
    imageError: boolean = false;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        public dialogRef: MatDialogRef<ImageLightboxDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ImageLightboxDialogData,
        private imageViewerService: ImageViewerService,
    ) {
        this.dialogRef.addPanelClass('image-lightbox-dialog');
        this.images = data.images || [data.imageUrl];
        this.currentIndex = data.currentIndex ?? 0;
        this.currentImageUrl = this.images[this.currentIndex] || data.imageUrl;
    }

    ngOnInit(): void {
        this.loadImage();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    async loadImage(): Promise<void> {
        this.loading = true;
        this.imageError = false;
        try {
            this.currentImageUrl = await this.imageViewerService.getImageUrl(this.images[this.currentIndex]);
            this.zoomLevel = 1; // Reset zoom ao trocar imagem
        } catch (error) {
            console.error('Erro ao carregar imagem:', error);
            this.imageError = true;
        } finally {
            this.loading = false;
        }
    }

    onImageLoad(): void {
        this.loading = false;
        this.imageError = false;
    }

    onImageError(): void {
        this.loading = false;
        this.imageError = true;
    }

    @HostListener('window:keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent): void {
        switch (event.key) {
            case 'Escape':
                this.close();
                break;
            case 'ArrowLeft':
                this.previousImage();
                break;
            case 'ArrowRight':
                this.nextImage();
                break;
            case '+':
            case '=':
                event.preventDefault();
                this.zoomIn();
                break;
            case '-':
                event.preventDefault();
                this.zoomOut();
                break;
        }
    }

    @HostListener('wheel', ['$event'])
    handleWheel(event: WheelEvent): void {
        if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            if (event.deltaY < 0) {
                this.zoomIn();
            } else {
                this.zoomOut();
            }
        }
    }

    previousImage(): void {
        if (this.images.length <= 1) return;
        this.currentIndex = this.currentIndex > 0 ? this.currentIndex - 1 : this.images.length - 1;
        this.loadImage();
    }

    nextImage(): void {
        if (this.images.length <= 1) return;
        this.currentIndex = this.currentIndex < this.images.length - 1 ? this.currentIndex + 1 : 0;
        this.loadImage();
    }

    zoomIn(): void {
        this.zoomLevel = Math.min(this.zoomLevel + 0.25, 5);
    }

    zoomOut(): void {
        this.zoomLevel = Math.max(this.zoomLevel - 0.25, 0.5);
    }

    resetZoom(): void {
        this.zoomLevel = 1;
    }

    openInNewTab(): void {
        const src = this.images[this.currentIndex] || this.data.imageUrl || this.currentImageUrl;
        if (src) {
            this.imageViewerService.openInNewTab(src);
        }
    }

    download(): void {
        const src = this.images[this.currentIndex] || this.data.imageUrl || this.currentImageUrl;
        if (src) {
            const fileName = `imagem-${this.currentIndex + 1}.${this.getImageExtension(src)}`;
            this.imageViewerService.downloadImage(src, fileName);
        }
    }

    private getImageExtension(url: string): string {
        const match = url.match(/\.(jpg|jpeg|png|gif|webp|bmp)(\?|$)/i);
        return match ? match[1] : 'jpg';
    }

    close(): void {
        this.dialogRef.close();
    }

    get canNavigatePrevious(): boolean {
        return this.images.length > 1;
    }

    get canNavigateNext(): boolean {
        return this.images.length > 1;
    }
}

